import MentorCard from 'hb-react/frontoffice/apps/Mentoring/components/MentorCard';
import { getMentorsProfiles } from 'hb-react/frontoffice/apps/Mentoring/requests';
import { type MentorsProfilesFetchParams } from 'hb-react/frontoffice/apps/Mentoring/requests/types';
import {
  type CustomAttributeType,
  type MentorProfileIndexCard,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { type FetchItemsParams } from 'hb-react/frontoffice/apps/Mentoring/utils/types';

export const makeCustomAttributesFilters = (filters) => {
  if (!filters) {
    return null;
  }

  const keys = Object.keys(filters);
  const customAttributesKeys = keys.filter((key) => key.startsWith('_'));

  if (customAttributesKeys.length === 0) {
    return null;
  }

  const customAttributesFilters = {};

  customAttributesKeys.forEach((key) => {
    customAttributesFilters[key] = filters[key].map(
      (elem: { label: string; value: string }) => elem.value,
    );
  });

  return customAttributesFilters;
};

export const makeFetchItemsParams = ({
  afterCursor,
  filters,
  topicId,
  requestedFields,
}: FetchItemsParams) => {
  const customAttributesFilters = makeCustomAttributesFilters(filters);

  return {
    order: 'desc',
    query: {
      statuses: ['accepted'],
      ...(filters?.keyword && { keyword: filters.keyword }),
      ...(filters?.skills && {
        skill_ids: filters.skills.map((skill) => skill.value),
      }),
      ...(filters?.location && { location: filters.location }),
      ...(customAttributesFilters && {
        customizable_attributes: customAttributesFilters,
      }),
    },
    limit: 20,
    ...(afterCursor
      ? { after_cursor: afterCursor }
      : { include_total_count: true }),
    topic_id: topicId,
    requested_fields: requestedFields,
  };
};

export const makeFetchItems =
  (program: Program) =>
  ({
    fetchFilters = { filters: {} },
    afterCursor,
    signal,
  }: MentorsProfilesFetchParams) => {
    const {
      id: programId,
      parent_id: parentId,
      parent_type: parentType,
    } = program;

    return getMentorsProfiles({
      programId,
      params: makeFetchItemsParams({
        afterCursor,
        filters: fetchFilters.filters,
        topicId: getProgramTopicId({ parentId, parentType }),
        requestedFields: [
          'id',
          'name',
          'location',
          'skills',
          'custom_attributes_card',
          'user_id',
          'photo_thumb_url',
          'relationship_with_current_user',
          'has_reached_mentees_limit',
        ],
      }),
      signal,
    });
  };

export const makeItemRenderer =
  ({
    program,
    disabledAccess,
    mentorCustomizableAttributes,
  }: {
    disabledAccess?: boolean;
    mentorCustomizableAttributes: Array<CustomAttributeType>;
    program: Program;
  }) =>
  ({ item }: { item: MentorProfileIndexCard }) => (
    <MentorCard
      mentorProfile={item}
      program={program}
      disabledAccess={disabledAccess}
      mentorCustomizableAttributes={mentorCustomizableAttributes}
    />
  );

export const ITEMS_BY_ROW = {
  xl: 4,
  l: 3,
  m: 2,
  s: 1,
};
